import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { User, Mail, Building, Save, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  reader: 'Lecteur',
  author: 'Auteur',
  reviewer: 'Évaluateur',
  admin: 'Administrateur'
};

const roleColors = {
  reader: 'bg-gray-100 text-gray-700',
  author: 'bg-blue-100 text-blue-700',
  reviewer: 'bg-purple-100 text-purple-700',
  admin: 'bg-green-100 text-green-700'
};

export default function ProfilePage() {
  const { user, getAuthHeaders, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    institution: user?.institution || '',
    orcid: user?.orcid || '',
    secondary_email: user?.secondary_email || ''
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        bio: user.bio || '',
        institution: user.institution || '',
        orcid: user.orcid || '',
        secondary_email: user.secondary_email || ''
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/users/me`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(form)
      });

      if (res.ok) {
        const updatedUser = await res.json();
        setUser({ ...user, ...updatedUser });
        toast.success('Profil mis à jour');
      }
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="profile-page" className="min-h-screen py-12 px-6 md:px-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold">Mon profil</h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos informations personnelles
          </p>
        </div>

        {/* Profile Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                  {user.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="font-serif text-xl font-semibold">{user.name}</h2>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={`${roleColors[user.role]} border-0`}>
                    {roleLabels[user.role] || user.role}
                  </Badge>
                  {user.orcid && (
                    <a 
                      href={`https://orcid.org/${user.orcid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <img src="https://orcid.org/assets/vectors/orcid.logo.icon.svg" alt="ORCID" className="w-4 h-4" />
                      {user.orcid}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Informations</CardTitle>
            <CardDescription>
              Modifiez vos informations personnelles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="pl-10"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email principal</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    value={user.email}
                    disabled
                    className="pl-10 bg-muted"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  L'adresse email principale ne peut pas être modifiée
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="secondary_email">Email secondaire</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="secondary_email"
                    placeholder="adresse@exemple.fr"
                    value={form.secondary_email}
                    onChange={(e) => setForm({ ...form, secondary_email: e.target.value })}
                    className="pl-10"
                    data-testid="profile-secondary-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="institution">Institution / Affiliation</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="institution"
                    placeholder="Université, clinique, cabinet..."
                    value={form.institution}
                    onChange={(e) => setForm({ ...form, institution: e.target.value })}
                    className="pl-10"
                    data-testid="profile-institution-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="orcid">ORCID</Label>
                <div className="relative">
                  <img 
                    src="https://orcid.org/assets/vectors/orcid.logo.icon.svg" 
                    alt="ORCID" 
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                  />
                  <Input
                    id="orcid"
                    placeholder="0000-0000-0000-0000"
                    value={form.orcid}
                    onChange={(e) => setForm({ ...form, orcid: e.target.value })}
                    className="pl-10"
                    data-testid="profile-orcid-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Votre identifiant ORCID (ex: 0009-0007-1950-7812).{' '}
                  <a href="https://orcid.org/register" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Créer un ORCID
                  </a>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Biographie</Label>
                <Textarea
                  id="bio"
                  placeholder="Présentez-vous en quelques lignes..."
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  rows={4}
                  data-testid="profile-bio-textarea"
                />
              </div>

              <Button type="submit" disabled={loading} className="gap-2" data-testid="save-profile-btn">
                <Save className="w-4 h-4" />
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Informations du compte</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Identifiant</span>
                <span className="font-mono text-xs">{user.user_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rôle</span>
                <span>{roleLabels[user.role]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Membre depuis</span>
                <span>{new Date(user.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
